// Thin wrapper around mixpanel-browser. Lazy-initialises so the SDK is only
// loaded if a project token is configured - keeps preview deploys quiet.
//
// Events fired (kept short and stable; renaming a property breaks the funnel
// in the Mixpanel UI):
//   - landing_viewed
//   - fit_submitted, fit_received
//   - email_gate_submitted   (client side; server-side reliable event lives
//                             in the unlock-email endpoint as a backup)
//   - diagnosis_started, diagnosis_completed
//   - models_revealed
//   - meeting_gate_viewed
//   - meeting_booked         (server-side only, fired from the Calendly webhook)

import mixpanel from 'mixpanel-browser';

const TOKEN: string =
  (import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined) ?? '';

let initialised = false;

function ensureInit(): boolean {
  if (initialised) return true;
  if (!TOKEN) return false;
  mixpanel.init(TOKEN, {
    // EU region - keeps PII on Mixpanel's EU infrastructure to align with the
    // privacy notice's data-location commitments.
    api_host: 'https://api-eu.mixpanel.com',
    persistence: 'localStorage',
    // Avoid the legacy /decide call - saves one request per page load.
    autocapture: false,
    track_pageview: false,
    debug: import.meta.env.DEV,
  });
  initialised = true;
  return true;
}

export function track(event: string, properties?: Record<string, unknown>): void {
  if (!ensureInit()) return;
  try {
    mixpanel.track(event, properties);
  } catch {
    // Analytics failure must never break the funnel.
  }
}

export function identify(email: string): void {
  if (!ensureInit()) return;
  try {
    // Use email as the canonical distinct_id so the anonymous pre-unlock
    // funnel ties cleanly to the lead post-unlock.
    mixpanel.identify(email);
    mixpanel.people.set({ $email: email });
  } catch {
    // ignore
  }
}
